import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Table, Button, Space, Modal, Form, Input, Switch,
    message, Popconfirm, Typography, Tag, Badge, Tooltip,
    Row, Col, Select, InputNumber, Flex, theme,
    Divider
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    FolderOutlined, CheckCircleOutlined, StopOutlined,
    CloseOutlined, SearchOutlined, FilterOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchProductCategories,
    createProductCategory,
    updateProductCategory,
    deleteProductCategory,
    updateProductCategoriesStatus,
    searchCategories,
    filterByStatus,
    filterByCode,
    filterByProductLine,
    changePagination,
    clearFilters,
    fetchAvailableCodes
} from '../actions';
import api from '../../../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CategorySettings = () => {
    const dispatch = useDispatch();
    const { token } = theme.useToken();

    // Add defensive programming for selector
    const productCategoriesState = useSelector(state => {
        console.log('Redux state:', state); // Debug log
        return state.categoryManagement?.productCategories || {
            categories: [],
            loading: false,
            pagination: { current: 1, pageSize: 50, total: 0 },
            filters: { searchText: '', statusFilter: 'all', codeFilter: 'all', productLineFilter: 'all' }
        };
    });

    // Ensure categories is always an array
    const { categories = [], loading = false, pagination = {}, filters = {} } = productCategoriesState;

    // Debug log to see what we're getting
    console.log('Categories data:', categories, 'Type:', typeof categories, 'Is Array:', Array.isArray(categories));
    console.log('Pagination state:', pagination);
    console.log('Filters state:', filters);

    const [modalVisible, setModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [form] = Form.useForm();

    // Local state for immediate UI updates (will be synced with Redux on API call)
    const [localSearchText, setLocalSearchText] = useState(filters.searchText || '');
    const [localStatusFilter, setLocalStatusFilter] = useState(filters.statusFilter || 'all');
    const [localCodeFilter, setLocalCodeFilter] = useState(filters.codeFilter || 'all');
    const [localProductLineFilter, setLocalProductLineFilter] = useState(filters.productLineFilter || 'all');
    const [productLines, setProductLines] = useState([]);

    // State for available codes
    const [availableCodes, setAvailableCodes] = useState([]);

    // State for managing category codes with tag system
    const [categoryTags, setCategoryTags] = useState([]);
    const [codeInputVisible, setCodeInputVisible] = useState(false);
    const [codeInputValue, setCodeInputValue] = useState('');
    const [editCodeInputIndex, setEditCodeInputIndex] = useState(-1);
    const [editCodeInputValue, setEditCodeInputValue] = useState('');
    const codeInputRef = useRef(null);
    const editCodeInputRef = useRef(null);

    // State for managing customization keywords with tag system
    const [keywordTags, setKeywordTags] = useState([]);
    const [keywordInputVisible, setKeywordInputVisible] = useState(false);
    const [keywordInputValue, setKeywordInputValue] = useState('');
    const [editKeywordInputIndex, setEditKeywordInputIndex] = useState(-1);
    const [editKeywordInputValue, setEditKeywordInputValue] = useState('');
    const keywordInputRef = useRef(null);
    const editKeywordInputRef = useRef(null);

    // State for managing title keywords with tag system
    const [titleKeywordTags, setTitleKeywordTags] = useState([]);
    const [titleKeywordInputVisible, setTitleKeywordInputVisible] = useState(false);
    const [titleKeywordInputValue, setTitleKeywordInputValue] = useState('');
    const [editTitleKeywordInputIndex, setEditTitleKeywordInputIndex] = useState(-1);
    const [editTitleKeywordInputValue, setEditTitleKeywordInputValue] = useState('');
    const titleKeywordInputRef = useRef(null);
    const editTitleKeywordInputRef = useRef(null);

    // Tag input style
    const tagInputStyle = {
        width: 64,
        height: 22,
        width: 120,
        marginInlineEnd: 8,
        verticalAlign: 'top',
    };

    const tagPlusStyle = {
        height: 22,
        background: token.colorBgContainer,
        borderStyle: 'dashed',
    };

    // Functions for managing category code tags
    const handleCodeClose = (removedTag) => {
        const newTags = categoryTags.filter(tag => tag !== removedTag);
        setCategoryTags(newTags);
    };

    const showCodeInput = () => {
        setCodeInputVisible(true);
    };

    const handleCodeInputChange = (e) => {
        setCodeInputValue(e.target.value);
    };

    const handleCodeInputConfirm = () => {
        if (codeInputValue && !categoryTags.includes(codeInputValue)) {
            setCategoryTags([...categoryTags, codeInputValue]);
        }
        setCodeInputVisible(false);
        setCodeInputValue('');
    };

    const handleEditCodeInputChange = (e) => {
        setEditCodeInputValue(e.target.value);
    };

    const handleEditCodeInputConfirm = () => {
        const newTags = [...categoryTags];
        newTags[editCodeInputIndex] = editCodeInputValue;
        setCategoryTags(newTags);
        setEditCodeInputIndex(-1);
        setEditCodeInputValue('');
    };

    // Functions for managing keyword tags
    const handleKeywordClose = (removedTag) => {
        const newTags = keywordTags.filter(tag => tag !== removedTag);
        setKeywordTags(newTags);
    };

    const showKeywordInput = () => {
        setKeywordInputVisible(true);
    };

    const handleKeywordInputChange = (e) => {
        setKeywordInputValue(e.target.value);
    };

    const handleKeywordInputConfirm = () => {
        if (keywordInputValue && !keywordTags.includes(keywordInputValue)) {
            setKeywordTags([...keywordTags, keywordInputValue]);
        }
        setKeywordInputVisible(false);
        setKeywordInputValue('');
    };

    const handleEditKeywordInputChange = (e) => {
        setEditKeywordInputValue(e.target.value);
    };

    const handleEditKeywordInputConfirm = () => {
        const newTags = [...keywordTags];
        newTags[editKeywordInputIndex] = editKeywordInputValue;
        setKeywordTags(newTags);
        setEditKeywordInputIndex(-1);
        setEditKeywordInputValue('');
    };

    // Functions for managing title keyword tags
    const handleTitleKeywordClose = (removedTag) => {
        const newTags = titleKeywordTags.filter(tag => tag !== removedTag);
        setTitleKeywordTags(newTags);
    };

    const showTitleKeywordInput = () => {
        setTitleKeywordInputVisible(true);
    };

    const handleTitleKeywordInputChange = (e) => {
        setTitleKeywordInputValue(e.target.value);
    };

    const handleTitleKeywordInputConfirm = () => {
        if (titleKeywordInputValue && !titleKeywordTags.includes(titleKeywordInputValue)) {
            setTitleKeywordTags([...titleKeywordTags, titleKeywordInputValue]);
        }
        setTitleKeywordInputVisible(false);
        setTitleKeywordInputValue('');
    };

    const handleEditTitleKeywordInputChange = (e) => {
        setEditTitleKeywordInputValue(e.target.value);
    };

    const handleEditTitleKeywordInputConfirm = () => {
        const newTags = [...titleKeywordTags];
        newTags[editTitleKeywordInputIndex] = editTitleKeywordInputValue;
        setTitleKeywordTags(newTags);
        setEditTitleKeywordInputIndex(-1);
        setEditTitleKeywordInputValue('');
    };

    // UseEffect for input focus management
    useEffect(() => {
        if (codeInputVisible) {
            codeInputRef.current?.focus();
        }
    }, [codeInputVisible]);

    useEffect(() => {
        editCodeInputRef.current?.focus();
    }, [editCodeInputValue]);

    useEffect(() => {
        if (keywordInputVisible) {
            keywordInputRef.current?.focus();
        }
    }, [keywordInputVisible]);

    useEffect(() => {
        editKeywordInputRef.current?.focus();
    }, [editKeywordInputValue]);

    useEffect(() => {
        if (titleKeywordInputVisible) {
            titleKeywordInputRef.current?.focus();
        }
    }, [titleKeywordInputVisible]);

    useEffect(() => {
        editTitleKeywordInputRef.current?.focus();
    }, [editTitleKeywordInputValue]);

    // Memoize functions to prevent recreation on every render
    const loadAvailableCodes = useCallback(async () => {
        const result = await dispatch(fetchAvailableCodes());
        if (result.success) {
            setAvailableCodes(result.data);
        }
    }, [dispatch]);

    const loadProductLines = useCallback(async () => {
        try {
            const response = await api.get('/product-lines?page=1&pageSize=500');
            setProductLines(response.data?.rows || []);
        } catch (error) {
            message.error('Không thể tải danh sách dòng sản phẩm');
        }
    }, []);

    // Load categories data on mount
    useEffect(() => {
        console.log('CategorySettings useEffect: Component mounted');
        console.log('Current categories length:', categories.length);

        // Only fetch if no data exists to prevent duplicate calls
        if (categories.length === 0) {
            console.log('No categories found, calling fetchProductCategories');
            dispatch(fetchProductCategories({
                page: 1,
                pageSize: 50,
                searchText: '',
                statusFilter: 'all',
                codeFilter: 'all',
                productLineFilter: 'all'
            }));
        } else {
            console.log('Categories already exist, skipping API call');
        }
    }, [dispatch, categories.length]);

    useEffect(() => {
        loadProductLines();
    }, [loadProductLines]);

    // Load available codes on mount
    useEffect(() => {
        console.log('CategorySettings useEffect: Loading available codes');
        console.log('Current available codes length:', availableCodes.length);

        // Only fetch if no codes exist to prevent duplicate calls
        if (availableCodes.length === 0) {
            console.log('No available codes found, calling loadAvailableCodes');
            loadAvailableCodes();
        } else {
            console.log('Available codes already exist, skipping API call');
        }
    }, [loadAvailableCodes, availableCodes.length]);

    // Sync local state with Redux state when filters change
    useEffect(() => {
        setLocalSearchText(filters.searchText || '');
        setLocalStatusFilter(filters.statusFilter || 'all');
        setLocalCodeFilter(filters.codeFilter || 'all');
        setLocalProductLineFilter(filters.productLineFilter || 'all');
    }, [filters.searchText, filters.statusFilter, filters.codeFilter, filters.productLineFilter]);

    const handleCreateCategory = () => {
        loadProductLines();
        setEditingCategory(null);
        form.resetFields();
        setCategoryTags([]); // Reset category tags for new category
        setKeywordTags([]); // Reset keyword tags for new category
        setTitleKeywordTags([]); // Reset title keyword tags for new category
        // Reset all input states
        setCodeInputVisible(false);
        setCodeInputValue('');
        setEditCodeInputIndex(-1);
        setEditCodeInputValue('');
        setKeywordInputVisible(false);
        setKeywordInputValue('');
        setEditKeywordInputIndex(-1);
        setEditKeywordInputValue('');
        setTitleKeywordInputVisible(false);
        setTitleKeywordInputValue('');
        setEditTitleKeywordInputIndex(-1);
        setEditTitleKeywordInputValue('');
        setModalVisible(true);
    };

    const handleEditCategory = (category) => {
        loadProductLines();
        setEditingCategory(category);
        // Set category tags based on existing category codes
        if (category.code && Array.isArray(category.code) && category.code.length > 0) {
            setCategoryTags([...category.code]);
        } else {
            setCategoryTags([]);
        }
        // Set keyword tags based on existing category keywords
        if (category.customizationInfoKeywords && Array.isArray(category.customizationInfoKeywords) && category.customizationInfoKeywords.length > 0) {
            setKeywordTags([...category.customizationInfoKeywords]);
        } else {
            setKeywordTags([]);
        }
        // Set title keyword tags based on existing category title keywords
        if (category.titleKeywords && Array.isArray(category.titleKeywords) && category.titleKeywords.length > 0) {
            setTitleKeywordTags([...category.titleKeywords]);
        } else {
            setTitleKeywordTags([]);
        }
        // Reset all input states
        setCodeInputVisible(false);
        setCodeInputValue('');
        setEditCodeInputIndex(-1);
        setEditCodeInputValue('');
        setKeywordInputVisible(false);
        setKeywordInputValue('');
        setEditKeywordInputIndex(-1);
        setEditKeywordInputValue('');
        setTitleKeywordInputVisible(false);
        setTitleKeywordInputValue('');
        setEditTitleKeywordInputIndex(-1);
        setEditTitleKeywordInputValue('');

        form.setFieldsValue({
            name: category.name,
            description: category.description,
            isActive: category.isActive,
            productLineId: category.productLineId || undefined,
            fbaBaseCost: category.fbaBaseCost,
            fbaInboundFeePerItem: category.fbaInboundFeePerItem,
            attributes: category.attributes ? JSON.stringify(category.attributes, null, 2) : ''
        });
        setModalVisible(true);
    };

    const handleDeleteCategory = async (id) => {
        try {
            await dispatch(deleteProductCategory(id));
            message.success('Xóa danh mục thành công');
            // Refresh data after deletion
            dispatch(fetchProductCategories({
                page: pagination.current || 1,
                pageSize: pagination.pageSize || 50,
                ...filters
            }));
        } catch (error) {
            message.error('Lỗi khi xóa danh mục');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            // Parse attributes JSON if provided
            let parsedAttributes = null;
            if (values.attributes && values.attributes.trim()) {
                try {
                    parsedAttributes = JSON.parse(values.attributes);
                } catch (error) {
                    message.error('Attributes phải là JSON hợp lệ');
                    return;
                }
            }

            // Use the tag arrays for codes and keywords
            const validCodes = categoryTags.filter(code => code && code.trim() !== '');
            const validKeywords = keywordTags.filter(keyword => keyword && keyword.trim() !== '');
            const validTitleKeywords = titleKeywordTags.filter(keyword => keyword && keyword.trim() !== '');

            const categoryData = {
                ...values,
                code: validCodes,  // Always send array, even if empty
                customizationInfoKeywords: validKeywords,  // Always send array, even if empty
                titleKeywords: validTitleKeywords,  // Always send array, even if empty
                attributes: parsedAttributes
            };

            if (editingCategory) {
                await dispatch(updateProductCategory(editingCategory.id, categoryData));
                message.success('Cập nhật danh mục thành công');
            } else {
                await dispatch(createProductCategory(categoryData));
                message.success('Tạo danh mục thành công');
            }

            setModalVisible(false);
            form.resetFields();
            setCategoryTags([]); // Reset category tags
            setKeywordTags([]); // Reset keyword tags
            setTitleKeywordTags([]); // Reset title keyword tags
            // Reset all input states
            setCodeInputVisible(false);
            setCodeInputValue('');
            setEditCodeInputIndex(-1);
            setEditCodeInputValue('');
            setKeywordInputVisible(false);
            setKeywordInputValue('');
            setEditKeywordInputIndex(-1);
            setEditKeywordInputValue('');
            setTitleKeywordInputVisible(false);
            setTitleKeywordInputValue('');
            setEditTitleKeywordInputIndex(-1);
            setEditTitleKeywordInputValue('');

            // Refresh data after create/update
            dispatch(fetchProductCategories({
                page: pagination.current || 1,
                pageSize: pagination.pageSize || 50,
                ...filters
            }));

            // Reload available codes
            loadAvailableCodes();
        } catch (error) {
            message.error('Có lỗi xảy ra');
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        form.resetFields();
        setEditingCategory(null);
        setCategoryTags([]); // Reset category tags
        setKeywordTags([]); // Reset keyword tags
        setTitleKeywordTags([]); // Reset title keyword tags
        // Reset all input states
        setCodeInputVisible(false);
        setCodeInputValue('');
        setEditCodeInputIndex(-1);
        setEditCodeInputValue('');
        setKeywordInputVisible(false);
        setKeywordInputValue('');
        setEditKeywordInputIndex(-1);
        setEditKeywordInputValue('');
        setTitleKeywordInputVisible(false);
        setTitleKeywordInputValue('');
        setEditTitleKeywordInputIndex(-1);
        setEditTitleKeywordInputValue('');
    };

    const handleStatusChange = async (id, isActive) => {
        try {
            await dispatch(updateProductCategoriesStatus([id], isActive));
            message.success(`${isActive ? 'Kích hoạt' : 'Vô hiệu hóa'} danh mục thành công`);
        } catch (error) {
            message.error('Lỗi khi cập nhật trạng thái');
        }
    };

    const getStatusBadge = (isActive) => {
        return isActive ? (
            <Badge status="success" text="Hoạt động" />
        ) : (
            <Badge status="default" text="Không hoạt động" />
        );
    };

    // Enhanced backend filtering handlers
    const handleSearchChange = (value) => {
        setLocalSearchText(value);
    };

    const handleSearchSubmit = (value) => {
        dispatch(searchCategories(value));
    };

    const handleStatusFilterChange = (value) => {
        setLocalStatusFilter(value);
        dispatch(filterByStatus(value));
    };

    const handleCodeFilterChange = (value) => {
        setLocalCodeFilter(value);
        dispatch(filterByCode(value));
    };

    const handleProductLineFilterChange = (value) => {
        setLocalProductLineFilter(value);
        dispatch(filterByProductLine(value));
    };

    const handleClearFilters = () => {
        setLocalSearchText('');
        setLocalStatusFilter('all');
        setLocalCodeFilter('all');
        setLocalProductLineFilter('all');
        dispatch(clearFilters());
    };

    // Handle table pagination changes
    const handleTableChange = (paginationInfo, filtersInfo, sorter) => {
        console.log('Table pagination change:', paginationInfo);
        console.log('Current filters:', filters);
        dispatch(changePagination(paginationInfo.current, paginationInfo.pageSize));
    };

    // Check if any filters are active
    const hasActiveFilters = localSearchText || localStatusFilter !== 'all' || localCodeFilter !== 'all' || localProductLineFilter !== 'all';

    // Count active filters
    const getActiveFiltersCount = () => {
        let count = 0;
        if (localSearchText) count++;
        if (localStatusFilter !== 'all') count++;
        if (localCodeFilter !== 'all') count++;
        if (localProductLineFilter !== 'all') count++;
        return count;
    };

    const columns = [
        {
            title: 'STT',
            key: 'index',
            width: 60,
            render: (_, __, index) => {
                const current = pagination.current || 1;
                const pageSize = pagination.pageSize || 50;
                return (current - 1) * pageSize + index + 1;
            }
        },
        {
            title: 'Tên danh mục',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <FolderOutlined style={{ color: '#1890ff' }} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Mã danh mục',
            dataIndex: 'code',
            key: 'code',
            width: 220,
            render: codes => {
                if (!codes || !Array.isArray(codes) || codes.length === 0) {
                    return '-';
                }
                return (
                    <Space wrap>
                        {codes.map((code, index) => (
                            <Tag color="blue" key={index}>{code}</Tag>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: 'Dòng sản phẩm',
            dataIndex: ['productLine', 'name'],
            key: 'productLine',
            width: 160,
            render: value => value || '-'
        },
        {
            title: 'Từ khóa Title',
            dataIndex: 'titleKeywords',
            key: 'titleKeywords',
            width: 200,
            render: keywords => {
                if (!keywords || !Array.isArray(keywords) || keywords.length === 0) {
                    return '-';
                }
                return (
                    <Space>
                        {keywords.map((keyword, index) => (
                            <Tag color="purple" key={index}>{keyword}</Tag>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: 'Từ khóa Customization',
            dataIndex: 'customizationInfoKeywords',
            key: 'customizationInfoKeywords',
            width: 200,
            render: keywords => {
                if (!keywords || !Array.isArray(keywords) || keywords.length === 0) {
                    return '-';
                }
                return (
                    <Space>
                        {keywords.map((keyword, index) => (
                            <Tag color="green" key={index}>{keyword}</Tag>
                        ))}
                    </Space>
                );
            }
        },
        {
            title: 'FBA Base Cost',
            dataIndex: 'fbaBaseCost',
            key: 'fbaBaseCost',
            width: 120,
            render: value => value ? `$${value}` : '-'
        },
        {
            title: 'FBA Inbound Fee / item',
            dataIndex: 'fbaInboundFeePerItem',
            key: 'fbaInboundFeePerItem',
            width: 170,
            render: value => value !== null && value !== undefined && value !== '' ? `$${value}` : '-'
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 130,
            render: (isActive, record) => (
                getStatusBadge(isActive)
            )
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: text => new Date(text).toLocaleDateString('vi-VN')
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 100,
            fixed: 'right',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Chỉnh sửa">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditCategory(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa danh mục này?"
                        onConfirm={() => handleDeleteCategory(record.id)}
                        okText="Xóa"
                        cancelText="Hủy"
                    >
                        <Tooltip title="Xóa">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="category-settings">
            {/* Enhanced Filter Section */}
            <Row style={{ marginBottom: 16 }} gutter={16} align="middle">
                <Col flex="auto">
                    <Space size="middle">
                        <Input.Search
                            style={{ width: 300 }}
                            placeholder="Tìm kiếm theo tên, mô tả hoặc mã danh mục..."
                            allowClear
                            value={localSearchText}
                            onChange={e => handleSearchChange(e.target.value)}
                            onSearch={handleSearchSubmit}
                        />

                        <Select
                            placeholder="Lọc theo trạng thái"
                            value={localStatusFilter}
                            onChange={handleStatusFilterChange}
                            style={{ width: 150 }}
                        >
                            <Option value="all">Tất cả trạng thái</Option>
                            <Option value="active">Hoạt động</Option>
                            <Option value="inactive">Không hoạt động</Option>
                        </Select>

                        <Select
                            placeholder="Lọc dòng sản phẩm"
                            value={localProductLineFilter}
                            onChange={handleProductLineFilterChange}
                            style={{ width: 180 }}
                            showSearch
                            optionFilterProp="label"
                            options={[
                                { value: 'all', label: 'Tất cả dòng sản phẩm' },
                                ...productLines.map(item => ({ value: item.id, label: item.name }))
                            ]}
                        />

                        <Select
                            placeholder="Lọc theo mã"
                            value={localCodeFilter}
                            onChange={handleCodeFilterChange}
                            style={{ width: 150 }}
                            showSearch
                            optionFilterProp="children"
                            loading={availableCodes.length === 0}
                        >
                            <Option value="all">Tất cả mã</Option>
                            {availableCodes.map(code => (
                                <Option key={code} value={code}>{code}</Option>
                            ))}
                        </Select>

                        {hasActiveFilters && (
                            <Button onClick={handleClearFilters} icon={<FilterOutlined />}>
                                Xóa bộ lọc ({getActiveFiltersCount()})
                            </Button>
                        )}
                    </Space>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreateCategory}
                    >
                        Thêm danh mục
                    </Button>
                </Col>
            </Row>

            {/* Active filters display */}
            {hasActiveFilters && (
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Space wrap>
                            <Text type="secondary">Bộ lọc đang áp dụng:</Text>
                            {localSearchText && (
                                <Tag color="blue" closable onClose={() => {
                                    setLocalSearchText('');
                                    dispatch(searchCategories(''));
                                }}>
                                    Tìm kiếm: {localSearchText}
                                </Tag>
                            )}
                            {localStatusFilter !== 'all' && (
                                <Tag color="green" closable onClose={() => handleStatusFilterChange('all')}>
                                    Trạng thái: {localStatusFilter === 'active' ? 'Hoạt động' : 'Không hoạt động'}
                                </Tag>
                            )}
                            {localCodeFilter !== 'all' && (
                                <Tag color="orange" closable onClose={() => handleCodeFilterChange('all')}>
                                    Mã: {localCodeFilter}
                                </Tag>
                            )}
                            {localProductLineFilter !== 'all' && (
                                <Tag color="cyan" closable onClose={() => handleProductLineFilterChange('all')}>
                                    Dòng sản phẩm: {productLines.find(item => item.id === localProductLineFilter)?.name || localProductLineFilter}
                                </Tag>
                            )}
                        </Space>
                    </Col>
                </Row>
            )}

            <Table
                dataSource={Array.isArray(categories) ? categories.map(category => ({ ...category, key: category.id })) : []}
                columns={columns}
                loading={loading}
                pagination={{
                    current: pagination.current || filters.page || 1,
                    pageSize: pagination.pageSize || filters.pageSize || 50,
                    total: pagination.total || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} danh mục`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    onChange: (page, pageSize) => {
                        console.log('Pagination onChange:', { page, pageSize, current: pagination.current });
                        dispatch(changePagination(page, pageSize));
                    },
                    onShowSizeChange: (current, size) => {
                        console.log('Pagination onShowSizeChange:', { current, size });
                        dispatch(changePagination(current, size));
                    }
                }}
                onChange={handleTableChange}
                className="category-table"
                scroll={{ x: 1000 }}
            />

            <Modal
                title={editingCategory ? 'Chỉnh sửa danh mục' : 'Thêm danh mục mới'}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={editingCategory ? 'Cập nhật' : 'Tạo mới'}
                cancelText="Hủy"
                width={700}
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                    className="category-form"
                >

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="isActive"
                                label="Trạng thái"
                                valuePropName="checked"
                                initialValue={true}
                            >
                                <Switch
                                    checkedChildren="Hoạt động"
                                    unCheckedChildren="Không hoạt động"
                                />
                            </Form.Item>
                        </Col>
                    </Row>


                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="name"
                                label="Tên danh mục"
                                rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
                            >
                                <Input placeholder="Nhập tên danh mục" prefix={<FolderOutlined />} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="productLineId" label="Dòng sản phẩm">
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="Chọn dòng sản phẩm"
                                    options={productLines.map(item => ({
                                        value: item.id,
                                        label: item.isActive ? item.name : `${item.name} (không hoạt động)`
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider style={{ margin: '10px 0' }} />
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Mã danh mục"
                                tooltip="Có thể thêm nhiều mã cho một danh mục. Nhấp đúp vào tag để chỉnh sửa."
                            >
                                <Flex gap="4px 0" wrap>
                                    {categoryTags.map((tag, index) => {
                                        if (editCodeInputIndex === index) {
                                            return (
                                                <Input
                                                    ref={editCodeInputRef}
                                                    key={tag}
                                                    size="small"
                                                    style={tagInputStyle}
                                                    value={editCodeInputValue}
                                                    onChange={handleEditCodeInputChange}
                                                    onBlur={handleEditCodeInputConfirm}
                                                    onPressEnter={handleEditCodeInputConfirm}
                                                />
                                            );
                                        }
                                        const isLongTag = tag.length > 20;
                                        const tagElem = (
                                            <Tag
                                                key={tag}
                                                closable
                                                color='blue'
                                                style={{ userSelect: 'none' }}
                                                onClose={() => handleCodeClose(tag)}
                                            >
                                                <span
                                                    onDoubleClick={e => {
                                                        setEditCodeInputIndex(index);
                                                        setEditCodeInputValue(tag);
                                                        e.preventDefault();
                                                    }}
                                                >
                                                    {isLongTag ? `${tag.slice(0, 20)}...` : tag}
                                                </span>
                                            </Tag>
                                        );
                                        return isLongTag ? (
                                            <Tooltip title={tag} key={tag}>
                                                {tagElem}
                                            </Tooltip>
                                        ) : (
                                            tagElem
                                        );
                                    })}
                                    {codeInputVisible ? (
                                        <Input
                                            ref={codeInputRef}
                                            type="text"
                                            style={tagInputStyle}
                                            value={codeInputValue}
                                            onChange={handleCodeInputChange}
                                            onBlur={handleCodeInputConfirm}
                                            onPressEnter={handleCodeInputConfirm}
                                            placeholder="Nhập mã"
                                        />
                                    ) : (
                                        <Tag style={tagPlusStyle} icon={<PlusOutlined />} onClick={showCodeInput}>
                                            Thêm mã
                                        </Tag>
                                    )}
                                </Flex>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider style={{ margin: '10px 0' }} />
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Từ khóa Customization Info"
                                tooltip="Có thể thêm nhiều từ khóa để xác định category dựa trên customizationInfo. Nhấp đúp vào tag để chỉnh sửa."
                            >
                                <Flex gap="4px 0" wrap>
                                    {keywordTags.map((tag, index) => {
                                        if (editKeywordInputIndex === index) {
                                            return (
                                                <Input
                                                    ref={editKeywordInputRef}
                                                    key={tag}
                                                    size="small"
                                                    style={tagInputStyle}
                                                    value={editKeywordInputValue}
                                                    onChange={handleEditKeywordInputChange}
                                                    onBlur={handleEditKeywordInputConfirm}
                                                    onPressEnter={handleEditKeywordInputConfirm}
                                                />
                                            );
                                        }
                                        const isLongTag = tag.length > 20;
                                        const tagElem = (
                                            <Tag
                                                key={tag}
                                                closable
                                                color="green"
                                                style={{ userSelect: 'none' }}
                                                onClose={() => handleKeywordClose(tag)}
                                            >
                                                <span
                                                    onDoubleClick={e => {
                                                        setEditKeywordInputIndex(index);
                                                        setEditKeywordInputValue(tag);
                                                        e.preventDefault();
                                                    }}
                                                >
                                                    {isLongTag ? `${tag.slice(0, 20)}...` : tag}
                                                </span>
                                            </Tag>
                                        );
                                        return isLongTag ? (
                                            <Tooltip title={tag} key={tag}>
                                                {tagElem}
                                            </Tooltip>
                                        ) : (
                                            tagElem
                                        );
                                    })}
                                    {keywordInputVisible ? (
                                        <Input
                                            ref={keywordInputRef}
                                            type="text"
                                            size="small"
                                            style={tagInputStyle}
                                            value={keywordInputValue}
                                            onChange={handleKeywordInputChange}
                                            onBlur={handleKeywordInputConfirm}
                                            onPressEnter={handleKeywordInputConfirm}
                                            placeholder="Nhập từ khóa"
                                        />
                                    ) : (
                                        <Tag style={tagPlusStyle} icon={<PlusOutlined />} onClick={showKeywordInput}>
                                            Thêm từ khóa
                                        </Tag>
                                    )}
                                </Flex>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider style={{ margin: '10px 0' }} />
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Từ khóa Title"
                                tooltip="Có thể thêm nhiều từ khóa để xác định category dựa trên title sản phẩm. Nhấp đúp vào tag để chỉnh sửa."
                            >
                                <Flex gap="4px 0" wrap>
                                    {titleKeywordTags.map((tag, index) => {
                                        if (editTitleKeywordInputIndex === index) {
                                            return (
                                                <Input
                                                    ref={editTitleKeywordInputRef}
                                                    key={tag}
                                                    size="small"
                                                    style={tagInputStyle}
                                                    value={editTitleKeywordInputValue}
                                                    onChange={handleEditTitleKeywordInputChange}
                                                    onBlur={handleEditTitleKeywordInputConfirm}
                                                    onPressEnter={handleEditTitleKeywordInputConfirm}
                                                />
                                            );
                                        }
                                        const isLongTag = tag.length > 20;
                                        const tagElem = (
                                            <Tag
                                                key={tag}
                                                closable
                                                color="purple"
                                                style={{ userSelect: 'none' }}
                                                onClose={() => handleTitleKeywordClose(tag)}
                                            >
                                                <span
                                                    onDoubleClick={e => {
                                                        setEditTitleKeywordInputIndex(index);
                                                        setEditTitleKeywordInputValue(tag);
                                                        e.preventDefault();
                                                    }}
                                                >
                                                    {isLongTag ? `${tag.slice(0, 20)}...` : tag}
                                                </span>
                                            </Tag>
                                        );
                                        return isLongTag ? (
                                            <Tooltip title={tag} key={tag}>
                                                {tagElem}
                                            </Tooltip>
                                        ) : (
                                            tagElem
                                        );
                                    })}
                                    {titleKeywordInputVisible ? (
                                        <Input
                                            ref={titleKeywordInputRef}
                                            type="text"
                                            size="small"
                                            style={tagInputStyle}
                                            value={titleKeywordInputValue}
                                            onChange={handleTitleKeywordInputChange}
                                            onBlur={handleTitleKeywordInputConfirm}
                                            onPressEnter={handleTitleKeywordInputConfirm}
                                            placeholder="Nhập từ khóa title"
                                        />
                                    ) : (
                                        <Tag style={tagPlusStyle} icon={<PlusOutlined />} onClick={showTitleKeywordInput}>
                                            Thêm từ khóa title
                                        </Tag>
                                    )}
                                </Flex>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider style={{ margin: '10px 0' }} />
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="fbaBaseCost"
                                label="FBA Base Cost ($)"
                                tooltip="Giá gốc tối thiểu cho các sản phẩm FBA trong category này"
                            >
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    placeholder="VD: 5.00"
                                    addonBefore="$"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="fbaInboundFeePerItem"
                                label="FBA Inbound Fee / item ($)"
                                tooltip="Chi phí inbound FBA ước tính trên mỗi sản phẩm; hiện chỉ dùng để lưu cấu hình"
                            >
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    style={{ width: '100%' }}
                                    placeholder="VD: 1.25"
                                    addonBefore="$"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="description"
                                label="Mô tả"
                            >
                                <TextArea
                                    rows={3}
                                    placeholder="Nhập mô tả chi tiết về danh mục"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="attributes"
                                label="Thuộc tính bổ sung (JSON)"
                                tooltip="Nhập dữ liệu JSON cho các thuộc tính bổ sung của danh mục"
                            >
                                <TextArea
                                    rows={4}
                                    placeholder={'{\n  "color": "#1890ff",\n  "icon": "folder",\n  "order": 1\n}'}
                                />
                            </Form.Item>
                        </Col>
                    </Row> */}
                </Form>
            </Modal>
        </div>
    );
};

export default CategorySettings;
